import React, { useMemo, useCallback } from 'react';
import { TextAreaRef } from 'antd/lib/input/TextArea';

import { MIN_HEIGHT } from './const';

import { TextAreaProps } from './types';

import { TextArea as StyledTextArea, Label, CaptionText } from './styled';

export const TextArea = React.forwardRef<TextAreaRef, Props>(
  (
    { label, error, caption, showCount, minHeight = MIN_HEIGHT, autoHeight, onKeyDown, ...props },
    ref,
  ) => {
    const formattedShowCount = useMemo(
      () =>
        typeof showCount === 'object'
          ? showCount
          : showCount
            ? { formatter: ({ count, maxLength }: IShowCount) => `${count}/${maxLength}` }
            : undefined,
      [showCount],
    );

    const onAutoHeight = useCallback(
      (e: { target: { style: { height: string }; scrollHeight: number } }) => {
        if (autoHeight) {
          e.target.style.height = 'inherit';

          const offset = e.target.scrollHeight === minHeight ? 0 : 20;
          e.target.style.height = `${e.target.scrollHeight + offset}px`;
        }
      },
      [autoHeight, minHeight],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        onKeyDown?.(e);
        onAutoHeight(e);
      },
      [onKeyDown, onAutoHeight],
    );

    return (
      <>
        {label && <Label>{label}</Label>}

        <StyledTextArea
          $hasError={Boolean(error)}
          {...props}
          ref={ref}
          showCount={formattedShowCount}
          onKeyDown={handleKeyDown}
          onKeyUp={onAutoHeight}
        />

        {caption && <CaptionText $hasError={Boolean(error)}>{error || caption}</CaptionText>}
      </>
    );
  },
);

export interface Props extends TextAreaProps {
  label?: string;
  error?: string;
  caption?: string;
  minHeight?: number;
  autoHeight?: boolean;
}

interface IShowCount {
  count?: number;
  maxLength?: number;
}
